import { useEffect, useState, useMemo } from "react";
import DateInput from '../../components/DateInput';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API from "../../api";
import { BASE_URL } from "../../config";
import { showSuccess } from "../../utils/dialog";
import { SignaturePanel } from "../../shared/SignaturePanel";
import "../../Styles/AccessibilityStndCustmer.css";
import talentLogo from "../../assets/talentlogo2.png";

const MediaRelease = () => {
  const navigate = useNavigate();
  const itemId = "media-release";
  const fieldOrder = useMemo(
    () => ["date", "firstName", "lastName", "employeeSignature"],
    []
  );
  const [formData, setFormData] = useState(() => {
    const accountId = localStorage.getItem("accountId");
    const saved = localStorage.getItem(`form_${accountId}_${itemId}`);
    const initial = saved ? JSON.parse(saved) : {};
    return fieldOrder.reduce((acc, f) => {
      acc[f] = initial[f] ?? "";
      return acc;
    }, {});
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [err, setErr] = useState("");

  useEffect(() => {
    const accountId = localStorage.getItem("accountId");
    const saved = localStorage.getItem(`form_${accountId}_${itemId}`);
    if (saved) {
      const raw = JSON.parse(saved);
      const normalized = fieldOrder.reduce((acc, f) => {
        acc[f] = raw[f] ?? "";
        return acc;
      }, {});
      setFormData(normalized);
    } else {
      // Prefill first/last name from profile when available
      (async () => {
        try {
          const resp = await API.get(`/api/registration/${accountId}/profile`);
          const profile = resp.data || {};
          const first = profile.first_name || localStorage.getItem('signup_first_name') || "";
          const last = profile.last_name || localStorage.getItem('signup_last_name') || "";
          const updated = { ...formData };
          if (first) updated.firstName = first;
          if (last) updated.lastName = last;
          setFormData(updated);
          localStorage.setItem(`form_${accountId}_${itemId}`, JSON.stringify(updated));
        } catch (err) {
          // ignore
        }
      })();
    }
  }, [fieldOrder]);

  // const isDateToday = (val) => {
  //   if (!val) return false;
  //   const input = new Date(val);
  //   const now = new Date();
  //   return (
  //     input.getFullYear() === now.getFullYear() &&
  //     input.getMonth() === now.getMonth() &&
  //     input.getDate() === now.getDate()
  //   );
  // };

  const isDateToday = (val) => {
    if (!val) return false;
    
    // Get current date in User's Local Time
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const day = String(now.getDate()).padStart(2, '0');

    // Create a standardized string "YYYY-MM-DD"
    const todayString = `${year}-${month}-${day}`;

    // Compare the strings directly
    return val === todayString;
  };

  useEffect(() => {
    const accountId = localStorage.getItem("accountId");
    const existing = (accountId ? localStorage.getItem(`status_${accountId}_${itemId}`) : null) || localStorage.getItem(`status_${itemId}`);
    const values = fieldOrder.map((f) => (formData[f] ?? "").toString().trim());
    const allEmpty = values.every((v) => v.length === 0);
    const statusToPersist = allEmpty ? "incomplete" : (existing === "complete" ? "complete" : "in-progress");
    localStorage.setItem(`status_${accountId}_${itemId}`, statusToPersist);
    localStorage.setItem(
      `form_${accountId}_${itemId}`,
      JSON.stringify(formData)
    );
    if (accountId && statusToPersist !== "incomplete") {
      axios
        .post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, {
          status: statusToPersist,
          formData: {},
        })
        .catch((err) => console.error("Failed to sync status:", err));
    }
  }, [formData, fieldOrder]);

  const handleSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();
    setErr("");
    setFieldErrors({});

    const errors = {};
    if (!formData.date) errors.date = "This field is required";
    else if (!isDateToday(formData.date)) errors.date = "Date must be today";
    if (!String(formData.firstName || "").trim())
      errors.firstName = "This field is required";
    if (!String(formData.lastName || "").trim())
      errors.lastName = "This field is required";
    if (!formData.employeeSignature)
      errors.employeeSignature = "Signature is required";

    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      setErr("Please fill the required fields!");
      return;
    }

    const accountId = localStorage.getItem("accountId");
    localStorage.setItem(
      `form_${accountId}_${itemId}`,
      JSON.stringify(formData)
    );
    const values = fieldOrder.map((f) => (formData[f] ?? "").toString().trim());
    const allEmpty = values.every((v) => v.length === 0);
    const allFilled = values.every((v) => v.length !== 0);
    const dateValid = isDateToday(formData.date);
    const signatureValid = !!(formData.employeeSignature && String(formData.employeeSignature).length > 0);
    const canBeComplete = allFilled && dateValid && signatureValid;
    const newStatus = allEmpty ? "incomplete" : canBeComplete ? "complete" : "in-progress";
    const payload = { status: newStatus, formData };
    if (accountId && newStatus === 'complete') {
      axios.post(`${BASE_URL}/api/registration/${accountId}/items/${itemId}/complete`, payload)
        .catch(err => console.error('Failed to save media-release:', err))
        .finally(() => {
          axios.post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, { status: newStatus, formData: {} }).catch(() => {});
          showSuccess("Form completed successfully!");
          navigate('/register');
        });
    } else {
      navigate('/register');
    }
  };

  const handleBack = () => {
    navigate("/register");
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      if (field === "date") {
        if (value && isDateToday(value)) delete next[field];
      } else {
        if (String(value || "").trim()) delete next[field];
      }
      return next;
    });
  };

  return (
    <div className="accessibility-form-page">
      <div className="form-container">
        {/* Top content card */}
        <div className="form-card">
          <header className="form-card-header" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <img src={talentLogo} alt="Talent Employment Inc." style={{ height: 64, marginBottom: 10 }} />
            <h2 className="form-title" style={{ textAlign: "center" }}>
              Media Release and Confidentiality Agreement
            </h2>
          </header>

          <section className="form-card-content">
            {err && (
              <div
                className="error-text"
                style={{ color: "#dc2626", marginBottom: 12 }}
              >
                {err}
              </div>
            )}
            <p className="form-intro">
              This Agreement is entered into by the undersigned, an employee of
              Talent Employment, on the date signed below.
            </p>
            <p className="form-intro paragraph-space">
              Workplace violence is defined under the Occupational Health and
              Safety Act as:
            </p>
            <ul className="form-bullets topbullets">
              <li>
                <strong>1)</strong> Consent to Use of Media: I grant Talent
                Employment permission to use my image, likeness, voice, and/or
                other recorded media (the "Media") for advertising, marketing,
                promotional, or business purposes, including print, online,
                social media, and other forms of media.
              </li>
              <li>
                <strong>2)</strong> Scope of Use: I agree that the Company may
                edit, alter, and/or combine my Media with other content as
                needed for these purposes. I acknowledge that I will not receive
                any compensation for the use of the Media unless otherwise
                agreed in writing. This consent is granted in perpetuity unless
                revoked in writing by me.
              </li>
              <li>
                <strong>3)</strong> Release of Liability: I release Talent
                Employment and its affiliates from any claims arising from the
                use of my Media as outlined in this Agreement.
              </li>
              <li>
                <strong>4)</strong> Voluntary Consent: I acknowledge that my
                consent is voluntary, and my refusal to participate will not
                affect my employment status or benefits.
              </li>
              <li>
                <strong>5)</strong> Confidentiality and Intellectual Property:
                Regardless of my consent, I will not share or distribute any
                media related to Talent Employment or its clients without
                written consent from both the client and Talent Employment
                management.
              </li>
              <li>
                <strong>6)</strong> Social Media Posts: Any unauthorized media
                post must be removed immediately upon request from Talent
                Employment or the client.
              </li>
            </ul>
            <p className="form-intro paragraph-space">
              Failure to comply with this Agreement may result in disciplinary
              action, including termination of employment. By signing below, I
              acknowledge that I have read, understood, and agree to the terms
              outlined in this Media Release and Confidentiality Agreement.
            </p>
          </section>

          <div className="grid-2">
            <div className="form-field">
              <label htmlFor="date">Date*</label>
              <DateInput
                id="date"
                value={formData.date}
                onChange={(v) => handleInputChange("date", v)}
                required
              />
              {fieldErrors.date && (
                <div
                  className="error-text"
                  style={{ color: "#dc2626", marginTop: 6 }}
                >
                  {fieldErrors.date}
                </div>
              )}
            </div>

            <div className="form-field">
              <label htmlFor="firstName">First Name*</label>
              <input
                id="firstName"
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange("firstName", e.target.value)}
                required
              />
              {fieldErrors.firstName && (
                <div
                  className="error-text"
                  style={{ color: "#dc2626", marginTop: 6 }}
                >
                  {fieldErrors.firstName}
                </div>
              )}
            </div>

            <div className="form-field">
              <label htmlFor="lastName">Last Name*</label>
              <input
                id="lastName"
                type="text"
                value={formData.lastName}
                onChange={(e) => handleInputChange("lastName", e.target.value)}
                required
              />
              {fieldErrors.lastName && (
                <div
                  className="error-text"
                  style={{ color: "#dc2626", marginTop: 6 }}
                >
                  {fieldErrors.lastName}
                </div>
              )}
            </div>
          </div>

          {/* Signature panel in a boxed wrapper */}
          <div className="signature-panel-wrapper">
            <SignaturePanel
              onSignatureChange={(dataURL) =>
                handleInputChange("employeeSignature", dataURL)
              }
              initialSignature={formData.employeeSignature}
              required
            />
            {fieldErrors.employeeSignature && (
              <div
                className="error-text"
                style={{ color: "#dc2626", marginTop: 6 }}
              >
                {fieldErrors.employeeSignature}
              </div>
            )}
          </div>

          <div className="form-buttons">
            <button type="button" onClick={handleBack}>
              Back
            </button>
            <button type="submit" onClick={handleSubmit}>
              Submit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaRelease;
